import React from 'react';
import { Popover, Transition } from '@headlessui/react';
import { DateRange } from 'react-date-range';
import dayjs from 'dayjs';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import { Colors } from '@core/config';

dayjs.extend(isSameOrBefore);

interface DateSelectorProps {
  startDate: Date | null;
  endDate: Date | null;
  onDateChange: (startDate: Date, endDate: Date) => void;
  primaryBrandColor?: string;
  startDateError?: string;
  endDateError?: string;
  onBlur: (e: React.FocusEvent<any>) => void;
  touched: boolean;
}

const DateSelector: React.FC<DateSelectorProps> = ({
  startDate,
  endDate,
  onDateChange,
  primaryBrandColor,
  startDateError,
  endDateError,
  onBlur,
  touched,
}) => {
  const hasError = touched && (startDateError || endDateError);
  const errorMessage = startDateError || endDateError;
  const formatDateDisplay = (
    startDate: Date | null,
    endDate: Date | null,
  ): string => {
    if (!startDate || !endDate) {
      return 'Select date';
    }

    const start = dayjs(startDate);
    const end = dayjs(endDate);

    if (end.isSameOrBefore(start, 'day')) {
      return start.format('D MMM YYYY');
    }

    return `${start.format('D MMM')} – ${end.format('D MMM YYYY')}`;
  };

  return (
    <div>
      <label className="mb-1 block text-sm font-medium">
        When do you need them?
      </label>
      <Popover className="relative">
        {({ open }) => (
          <>
            <Popover.Button
              className={`flex w-full items-center justify-between rounded-sm border bg-white px-3 py-2 text-sm ${
                open ? 'ring-2 ring-purple-500' : ''
              }`}
              onBlur={onBlur}
            >
              <span className="block truncate">
                {formatDateDisplay(startDate, endDate)}
              </span>
              <svg
                className="ml-2 h-5 w-5 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                />
              </svg>
            </Popover.Button>
            <Transition
              as={React.Fragment}
              enter="transition ease-out duration-200"
              enterFrom="opacity-0 translate-y-1"
              enterTo="opacity-100 translate-y-0"
              leave="transition ease-in duration-150"
              leaveFrom="opacity-100 translate-y-0"
              leaveTo="opacity-0 translate-y-1"
            >
              <Popover.Panel className="absolute z-10 mt-2 w-full rounded-lg border bg-white shadow-lg">
                <div className="p-4">
                  <DateRange
                    ranges={[
                      {
                        startDate: startDate || new Date(),
                        endDate: endDate || new Date(),
                        key: 'selection',
                      },
                    ]}
                    onChange={(ranges: any) => {
                      const { startDate: newStartDate, endDate: newEndDate } =
                        ranges.selection;
                      onDateChange(newStartDate, newEndDate);
                    }}
                    moveRangeOnFirstSelection={false}
                    rangeColors={[primaryBrandColor || Colors.LINK_BLUE]}
                    showDateDisplay={false}
                  />
                </div>
              </Popover.Panel>
            </Transition>
            {hasError && (
              <div className="mt-1 text-xs text-red-500">{errorMessage}</div>
            )}
          </>
        )}
      </Popover>
    </div>
  );
};

export default DateSelector;
